import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import JSZip from "jszip";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { convertInvoicesToCSV, downloadCSV } from "@/utils/csvUtils";
import { useTranslation } from "react-i18next";

interface BulkActionsProps {
  selectedInvoices: string[];
  onBulkActionSelect: (action: string | null) => void;
  onDeleteClick: () => void;
}

export const BulkActions = ({
  selectedInvoices,
  onBulkActionSelect,
  onDeleteClick,
}: BulkActionsProps) => {
  const { t } = useTranslation();
  const handleBulkDownload = async () => {
    if (selectedInvoices.length === 0) {
      toast.error(t("bulk_actions.no_invoices_selected", "No invoices selected"));
      return;
    }

    try {
      const zip = new JSZip();
      
      const { data: invoices, error: fetchError } = await supabase
        .from("invoices")
        .select("invoice_number, file_path")
        .in("id", selectedInvoices);

      if (fetchError) throw fetchError;
      if (!invoices || invoices.length === 0) {
        throw new Error(t("bulk_actions.no_invoices_found", "No invoices found"));
      }

      const downloadPromises = invoices.map(async (invoice) => {
        if (!invoice.file_path) return;
        
        try {
          const filePath = invoice.file_path.includes("public/invoices/")
            ? invoice.file_path.split("public/invoices/")[1]
            : invoice.file_path;

          const { data, error } = await supabase.storage
            .from("invoices")
            .download(filePath);

          if (error) {
            console.error("Download error for invoice:", invoice.invoice_number, error);
            throw error;
          }

          if (data) {
            zip.file(`invoice_${invoice.invoice_number}.pdf`, data);
          }
        } catch (error) {
          console.error("Error processing invoice:", invoice.invoice_number, error);
          toast.error(t("toasts.failed_to_process_invoice", { invoice_number: invoice.invoice_number }));
        }
      });

      await Promise.all(downloadPromises);

      const content = await zip.generateAsync({ type: "blob" });
      const url = window.URL.createObjectURL(content);
      const a = document.createElement("a");
      a.href = url;
      a.download = "invoices.zip";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(t("toasts.invoice_downloaded_successfully", "Invoices downloaded successfully"));
    } catch (error) {
      console.error("Bulk download error:", error);
      toast.error(t("toasts.invoice_downloaded_failed", "Failed to download invoices"));
    }
  };

  const handleExportCSV = async () => {
    if (selectedInvoices.length === 0) {
      toast.error(t("toasts.no_invoices_selected", "No invoices selected"));
      return;
    }

    try {
      const { data: invoices, error } = await supabase
        .from('invoices')
        .select('*')
        .in('id', selectedInvoices);

      if (error) throw error;

      if (!invoices || invoices.length === 0) {
        toast.error(t("bulk_actions.no_invoices_found", "No invoices found"));
        return;
      }

      const csvContent = convertInvoicesToCSV(invoices);
      downloadCSV(csvContent, `invoices_export_${new Date().toISOString().split('T')[0]}.csv`);
      
      toast.success(t("toasts.invoice_exported_successfully", "Invoices exported successfully"));
    } catch (error) {
      console.error('CSV export error:', error);
      toast.error(t("toasts.invoice_exported_failed", "Failed to export invoices"));
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="ml-auto bg-white hover:bg-[#5CC2BB] hover:text-[#34375B] hover:border-transparent hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-[1px] hover:-translate-y-[1px] transition-all duration-200"
          disabled={selectedInvoices.length === 0}
        >
          {t("bulk_actions.title")} <ChevronDown className="ml-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem 
          onClick={() => {
            if (selectedInvoices.length === 0) {
              toast.error(t("bulk_actions.no_invoices_selected", "No invoices selected"));
              return;
            }
            onBulkActionSelect("schedule");
          }}
        >
          {t("bulk_actions.schedule", "Schedule Selected")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleBulkDownload}>
          {t("bulk_actions.download", "Download Selected")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleExportCSV}>
          {t("bulk_actions.export", "Export as CSV")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onDeleteClick} className="text-destructive">
          {t("bulk_actions.delete", "Delete Selected")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};